// Classifies whether the user's reply to a pending checkpoint summary reads
// as confirmation or rejection. Mirrors modeRouter.js's pure-rule-first,
// LLM-fallback pattern.
import { userTurns } from "./chatHistory";
import { renderStr, ownVocab } from "./prompts";
import { wordRe, perLang, answerRes } from "./langRegex";

// Boundaries use Unicode-letter lookaround, not \b (see langRegex.js's
// wordRe): JS's \b treats \w as ASCII-only, so it fails to see a word edge
// next to accented vowels (e.g. "sì" or "così" followed by a comma never
// matches with a trailing \b, unlike Python's re module which is
// Unicode-aware by default — see backend/pocketflow_agents.py's equivalent
// regexes). Bodies come from vocab(lang).checkpoint_confirm/_reject.
const checkpointRes = (lang) => perLang("checkpoint", lang, (v) => ({
  confirm: wordRe(v.checkpoint_confirm),
  reject: wordRe(v.checkpoint_reject),
}));

// Optional per-language negation guard: vocab key negation_markers, read from
// the language's OWN vocab entry (ownVocab — the merged vocab() would leak
// EN's key into it). Italian defines none, so for it this is always null and
// the behaviour is unchanged. Global flag for scanning every occurrence.
const negationRe = (lang) => perLang("negation_markers", lang, () => {
  const body = ownVocab(lang).negation_markers;
  return body ? new RegExp(wordRe(body).source, "giu") : null;
});

// True if a negation marker occurs outside [start, end) — the confirm match.
function negatedOutside(text, re, start, end) {
  for (const m of text.matchAll(re)) {
    if (m.index < start || m.index + m[0].length > end) return true;
  }
  return false;
}

// Pure: true/false/null from keyword matches in the user's last message.
// A confirm match is downgraded to null (ambiguous -> LLM classifier) when
// the language defines negation_markers and one occurs outside the confirm
// match itself ("I wouldn't say that's right"); a marker inside a matched
// confirm idiom ("couldn't agree more") does not count.
export function pickCheckpointReply({ chat, lang = "it" }) {
  const turns = userTurns(chat);
  const lastUser = turns[turns.length - 1] || "";
  const { confirm, reject } = checkpointRes(lang);
  const confirmM = confirm.exec(lastUser);
  const rejectM = reject.exec(lastUser);
  // Earliest match wins; on a tie (both start at the same position, e.g. EN
  // "yes, but…" in checkpoint_reject vs "yes") reject wins — never force an
  // advance on a hedged reply. The Italian bodies can't tie (no confirm and
  // reject alternative can start at the same position), so IT is unchanged.
  if (rejectM && (!confirmM || rejectM.index <= confirmM.index)) return false;
  if (confirmM) {
    const neg = negationRe(lang);
    if (neg && negatedOutside(lastUser, neg, confirmM.index, confirmM.index + confirmM[0].length)) return null;
    // Question guard (own-vocab flag question_is_ambiguous, EN only): "is
    // that right?" carries a confirm word but asks rather than confirms.
    if (ownVocab(lang).question_is_ambiguous && lastUser.includes("?")) return null;
    return true;
  }
  return null;
}

// LLM fallback for an ambiguous reply. Returns true/false/null — null (not
// a default) on no match or failure, treated identically to false by the
// caller (never force an advance on ambiguous input).
export async function classifyCheckpointReply({ chat, generate, lang = "it" }) {
  try {
    const raw = await generate(renderStr("checkpoint_classifier", lang, { chat }));
    const lower = (raw || "").toLowerCase();
    const candidates = [];
    for (const [re, value] of answerRes(lang)) {
      const m = re.exec(lower);
      if (m) candidates.push([m.index, value]);
    }
    if (!candidates.length) return null;
    candidates.sort((a, b) => a[0] - b[0]);
    return candidates[0][1];
  } catch (e) {
    return null;
  }
}
